import type {Config, CsrfConfig, ProjectTimeRecord, TaskOption, TaskWithChildren} from '../types/types';
import {getMessage} from '../utils/messages';
import {Temporal} from "@js-temporal/polyfill";

type RequestOptions = {
	method: 'GET' | 'POST' | 'PUT' | 'DELETE';
	body?: string;
};


const buildHeaders = (csrf?: CsrfConfig): HeadersInit => {
	const headers: HeadersInit = {
		'Content-Type': 'application/json',
	};

	if (csrf) {
		headers[csrf.headerName] = csrf.token;
	}

	return headers;
};

const handleResponse = async <T>(response: Response): Promise<T> => {
	if (!response.ok) {
		const message = await response.text();
		throw new Error(message || getMessage('monthlyRecording.api.error.default'));
	}

	if (response.status === 204) {
		return undefined;
	}

	try {
		return (await response.json()) as T;
	} catch {
		throw new Error(getMessage('monthlyRecording.api.error.parse'));
	}
};

const request = async <T>(config: Config, path: string, options: RequestOptions): Promise<T> => {
	const response = await fetch(`${config.baseUrl}${path}`, {
		credentials: 'same-origin',
		method: options.method,
		body: options.body,
		headers: buildHeaders(config.csrf),
	});
	return handleResponse<T>(response);
};

export const listProjectTimes = (config: Config, yearMonth: Temporal.PlainYearMonth) => {
	const params = new URLSearchParams({yearMonth: yearMonth.toString()});
	return request<ProjectTimeRecord[]>(config, `/monthly-recording/entries?${params.toString()}`, {method: 'GET'});
};

export const createProjectTime = (config: Config, entry: ProjectTimeRecord) => {
	return request<ProjectTimeRecord>(config, '/monthly-recording/entries', {
		method: 'POST',
		body: JSON.stringify(entry),
	});
};

export const updateProjectTime = (config: Config, entry: ProjectTimeRecord) => {
	if (!entry.id) {
		throw new Error(getMessage('monthlyRecording.api.error.missingId'));
	}
	return request<ProjectTimeRecord>(config, `/monthly-recording/entries/${entry.id}`, {
		method: 'PUT',
		body: JSON.stringify(entry),
	});
};

export const deleteProjectTime = (config: Config, id: number) => {
	return request<void>(config, `/monthly-recording/entries/${id}`, {
		method: 'DELETE',
	});
};

export const loadTasks = (config: Config, parentId?: number) => {
	const params = parentId != null ? `?${new URLSearchParams({parentId: parentId.toString()}).toString()}` : '';
	return request<TaskOption[]>(config, `/monthly-recording/tasks${params}`, {method: 'GET'});
};

export const filterTasks = (config: Config, query: string) => {
	const params = new URLSearchParams({query});
	return request<TaskWithChildren[]>(config, `/recording/filterTasks.json?${params.toString()}`, {method: 'GET'});
};
